/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { Logger } from '@feature-hub/logger';
import type {
  ContentServiceV1,
  GfaLocaleServiceV1,
  RenderModeServiceV1,
  TrackingServiceV2,
} from './types/feature-service-types';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import App from './FeatureApp';
import { ContentContextProvider } from './context/ContentContext';
import { getDesignLibrary } from './AlphaHelper';
import { TrackingServiceContextProvider } from './context/TrackingServiceContext';

export interface FeatureDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'gfa:locale-service': GfaLocaleServiceV1;
  readonly 'audi-render-mode-service'?: RenderModeServiceV1;
  readonly 'audi-content-service'?: ContentServiceV1;
  readonly 'audi-tracking-service'?: TrackingServiceV2;
}

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureDependencies> = {
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
      'audi-content-service': '^1.0.0',
    },
    externals: {
      react: '^16.13.1 || ^17.0.2 || ^18',
      'react-dom': '^16.13.1 || ^17.0.2 || ^18',
      'styled-components': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.8.0' }),
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-render-mode-service': '^1.0.0',
      'audi-tracking-service': '^2.0.0',
    },
  },

  create: ({ featureServices }: FeatureAppEnvironment<FeatureDependencies, void>) => {
    const loggerService = featureServices['s2:logger'];
    const contentService = featureServices['audi-content-service'];
    const trackingService = featureServices['audi-tracking-service'];
    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__ || 'fa-nar-secondary-links';
    }
    loggerService?.info('Feature App created.');

    return {
      render: () => {
        if (!contentService) {
          throw new Error('Content service is required');
        }
        return (
          <UniversalEditorProvider
            contentService={contentService}
            renderModeService={featureServices['audi-render-mode-service']}
          >
            <ContentContextProvider contentService={contentService} loggerService={loggerService}>
              {trackingService ? (
                <TrackingServiceContextProvider trackingService={trackingService}>
                  <App />
                </TrackingServiceContextProvider>
              ) : (
                <App />
              )}
            </ContentContextProvider>
          </UniversalEditorProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
